<template>
  <div
    class="ui-textarea"
    :class="[
      `ui-textarea--${size}`,
      {
        'ui-textarea--disabled': disabled,
        'ui-textarea--readonly': readonly,
        'ui-textarea--error': error,
        'ui-textarea--resizable': resizable && !autoGrow,
        'ui-textarea--block': block,
        'ui-textarea--has-value': textareaHasValue,
        'ui-textarea--auto-grow': autoGrow
      }
    ]"
  >
    <label v-if="label" class="ui-textarea__label" :for="id">
      {{ label }}
      <span v-if="required" class="ui-textarea__required">*</span>
    </label>

    <div class="ui-textarea__wrapper">
      <textarea
        ref="textareaRef"
        :id="id"
        :value="modelValue"
        :placeholder="fieldPlaceholder"
        :disabled="disabled"
        :required="required"
        :readonly="readonly"
        :maxlength="maxlength"
        :rows="autoGrow ? null : rows"
        :name="name"
        class="ui-textarea__field"
        :data-invalid-eval-anchor="invalidEvalAnchor || undefined"
        :class="{ 'ui-textarea__field--auto-grow': autoGrow }"
        :style="autoGrowStyle"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @change="handleChange"
      ></textarea>

      <div v-if="maxlength && showCount" class="ui-textarea__counter">{{ modelValue?.length || 0 }}/{{ maxlength }}</div>
    </div>

    <div v-if="error" class="ui-textarea__error">{{ errorMessage }}</div>
    <div v-else-if="helperText" class="ui-textarea__helper">{{ helperText }}</div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, nextTick, computed } from 'vue';
import { lengthbCut } from '~/composables/useCommon.js';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  rows: {
    type: [String, Number],
    default: 4
  },
  size: {
    type: String,
    default: 'medium',
    validator: value => ['small', 'medium', 'large', 'xlarge'].includes(value)
  },
  disabled: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  required: {
    type: Boolean,
    default: false
  },
  block: {
    type: Boolean,
    default: false
  },
  error: {
    type: Boolean,
    default: false
  },
  errorMessage: {
    type: String,
    default: ''
  },
  helperText: {
    type: String,
    default: ''
  },
  id: {
    type: String,
    default: () => `ui-textarea-${Math.random().toString(36).substring(2, 9)}`
  },
  name: {
    type: String,
    default: ''
  },
  maxlength: {
    type: [String, Number],
    default: undefined
  },
  /** UTF-8 바이트 기준 최대 길이 (UiInput의 maxBytes와 동일) */
  maxBytes: {
    type: Number,
    default: undefined,
  },
  showCount: {
    type: Boolean,
    default: false
  },
  resizable: {
    type: Boolean,
    default: true
  },
  // 자동 높이 조절 관련 props
  autoGrow: {
    type: Boolean,
    default: false
  },
  minHeight: {
    type: [String, Number],
    default: 100
  },
  maxHeight: {
    type: [String, Number],
    default: null
  },
  /** 평가 유효성 스크롤·깜빡임: 네이티브 textarea 요소에만 부여 */
  invalidEvalAnchor: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:modelValue', 'focus', 'blur', 'change']);

const fieldPlaceholder = computed(() => (props.readonly || props.disabled ? '' : props.placeholder));

// textarea 요소 참조
const textareaRef = ref(null);

// 내부 상태로 입력값 추적
const textareaHasValue = ref(false);

// autoGrow 모드일 때 동적 스타일 계산
const autoGrowStyle = computed(() => {
  if (!props.autoGrow) return {};

  return {
    minHeight: `${props.minHeight}px`,
    maxHeight: props.maxHeight ? `${props.maxHeight}px` : 'none',
    overflowY: props.maxHeight ? 'auto' : 'hidden'
  };
});

// 높이 자동 조절 함수
const adjustHeight = () => {
  if (!props.autoGrow || !textareaRef.value) return;

  const textarea = textareaRef.value;

  // 높이 초기화 후 scrollHeight 계산
  textarea.style.height = 'auto';

  // scrollHeight + 여유(서브픽셀·border로 인한 잘림 방지)
  let newHeight = textarea.scrollHeight + 2;

  // minHeight 적용
  const minH = Number(props.minHeight);
  if (newHeight < minH) {
    newHeight = minH;
  }

  // maxHeight 적용
  if (props.maxHeight) {
    const maxH = Number(props.maxHeight);
    if (newHeight > maxH) {
      newHeight = maxH;
    }
  }

  textarea.style.height = `${newHeight}px`;
};

// 컴포넌트 마운트·표시 후 높이 설정 (아코디언 펼침 등 지연 렌더 대응)
const scheduleAdjustHeight = () => {
  if (!props.autoGrow) return;
  nextTick(() => {
    adjustHeight();
    requestAnimationFrame(() => {
      adjustHeight();
    });
  });
};

// 이벤트 핸들러 함수들
const handleInput = event => {
  let newValue = event.target.value;
  // type="number"일 때 숫자 외 문자 제거
  if (props.type === 'number') {
    newValue = newValue.replace(/[^0-9.-]/g, '');
    event.target.value = newValue;
  } else if (props.maxBytes != null && props.maxBytes > 0) {
    const trimmed = lengthbCut(String(newValue ?? ''), Number(props.maxBytes));
    if (trimmed !== newValue) {
      event.target.value = trimmed;
      newValue = trimmed;
    }
  }
  textareaHasValue.value = newValue.length > 0;
  emit('update:modelValue', newValue);

  if (props.autoGrow) {
    scheduleAdjustHeight();
  }
};

// modelValue 변경 시 높이 재조정 (외부에서 값이 변경될 때)
watch(
  () => props.modelValue,
  () => {
    scheduleAdjustHeight();
  }
);

onMounted(scheduleAdjustHeight);

const handleFocus = event => {
  emit('focus', event);
};

const handleBlur = event => {
  emit('blur', event);
};

const handleChange = event => {
  emit('change', event);
};
</script>

<style lang="scss" scoped>
.ui-textarea {
  display: flex;
  flex-direction: column;
  gap: $spacing-xs;

  // 값이 입력되었을 때 스타일
  &--has-value {
    .ui-textarea__field {
      border: 1px solid $border-color-filled;
    }
  }

  &__label {
    font-size: $font-size-sm;
    font-weight: 500;
    color: var(--color-gray-70, #464c53);
    margin-bottom: 4px;
  }

  &__required {
    color: var(--color-system-r30, #f30);
    margin-left: 2px;
  }

  &__wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
  }

  &__field {
    width: 100%;
    padding: $spacing-sm;
    border: 1px solid var(--color-gray-20, #cdd1d5);
    border-radius: 6px;
    background-color: var(--color-gray-0, #fff);
    font-size: $font-size-sm;
    color: var(--color-gray-70, #464c53);
    resize: none;
    transition: all $transition-normal ease;
    line-height: 1.5;
    min-height: 100px;

    &:focus {
      outline: none !important;
      border-color: var(--color-system-b30, #0084ff);
      box-shadow: none;
    }

    &::placeholder {
      color: $placeholder-color;
      font-family: $placeholder-font-family;
      font-size: $ui-font-md;
      font-style: normal;
      font-weight: $placeholder-font-weight;
      line-height: $placeholder-line-height;
    }
  }

  &__counter {
    position: absolute;
    bottom: $spacing-xs;
    right: $spacing-xs;
    font-size: $font-size-xs;
    color: var(--color-gray-40, #8a949e);
    background-color: rgba(255, 255, 255, 0.8);
    padding: 2px 4px;
    border-radius: 4px;
  }

  &__error,
  &__helper {
    margin-top: 2px;
  }

  &__error {
    font-size: $font-size-sm;
    color: $system-red;
  }

  &__helper {
    font-size: $font-size-xs;
    color: var(--color-gray-60, #58616a);
  }

  // 크기 변형 - Small
  &--small {
    .ui-textarea__field {
      padding: $ui-padding-sm;
      font-size: $ui-font-sm;
      min-height: 80px;

      &::placeholder {
        font-size: $ui-font-sm;
      }
    }
    .ui-textarea__label {
      @include font-style($body-small);
    }
  }

  // 크기 변형 - Medium
  &--medium {
    .ui-textarea__field {
      padding: $ui-padding-sm;
      font-size: $ui-font-md;
      min-height: 100px;

      &::placeholder {
        font-size: $ui-font-md;
      }
    }
    .ui-textarea__label {
      @include font-style($body-medium);
    }
  }

  // 크기 변형 - Large
  &--large {
    .ui-textarea__field {
      padding: $ui-padding-lg;
      font-size: $ui-font-lg;
      min-height: 120px;

      &::placeholder {
        font-size: $ui-font-lg;
      }
    }
    .ui-textarea__label {
      @include font-style($body-large-bold);
    }
  }

  // 크기 변형 - XLarge
  &--xlarge {
    .ui-textarea__field {
      padding: $ui-padding-xl;
      font-size: $ui-font-xl;
      min-height: 140px;
      border-radius: 4px;

      &::placeholder {
        font-size: $ui-font-xl;
      }
    }
    .ui-textarea__label {
      @include font-style($body-xlarge-bold);
      font-size: $font-size-xxl;
    }
  }

  // 수정자
  &--block {
    width: 100%;
  }

  &--resizable {
    .ui-textarea__field {
      resize: vertical;
    }
  }

  // 자동 높이 조절 모드 — size별 min-height(100px 등) 제거, JS adjustHeight만 사용
  &--auto-grow {
    .ui-textarea__field {
      min-height: unset;
      height: auto;
    }
  }

  &__field--auto-grow {
    resize: none !important; // 자동 조절 시 수동 리사이즈 비활성화
    overflow-y: hidden; // 기본적으로 스크롤 숨김 (maxHeight 설정 시 JS에서 auto로 변경)
    transition: height 0.1s ease-out; // 부드러운 높이 전환
  }

  // 상태 변형
  &--disabled {
    .ui-textarea__field {
      border: 1px solid var(--color-gray-20, #cdd1d5);
      background: var(--color-gray-10, #e6e8ea);
      color: var(--color-gray-30, #b1b8be);
      cursor: not-allowed;
    }
  }

  &--error {
    .ui-textarea__field {
      border: 1px solid var(--color-system-r30, #f30);
      color: var(--color-gray-70, #464c53);

      &:focus {
        outline: none;
        border-color: var(--color-system-r30, #f30);
      }
    }
  }

  // readonly 상태 스타일 추가 (UiInput과 동일)
  &--readonly {
    .ui-textarea__field {
      background: var(--color-gray-10, #e6e8ea); // 읽기 전용 배경
      border: 1px solid $border-color-light;
      color: var(--color-gray-60, #58616a);
      cursor: default;
      word-break: break-word; // 긴 텍스트 줄바꿈
      white-space: pre-wrap; // 줄바꿈 유지하면서 자동 래핑
    }
  }
}
</style>
